import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Plus, Pencil, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api, { formatDate } from '@/lib/api';

const EMPTY_FORM = { name: '', email: '', phone: '', address: '', city: '', state: '', zip: '', country: '', gst_number: '' };

export default function ClientsPage() {
  const [clients, setClients] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('clients');

  const fetchData = useCallback(async () => {
    try {
      const [c, v] = await Promise.all([api.get('/clients'), api.get('/vendors')]);
      setClients(c.data);
      setVendors(v.data);
    } catch { toast.error('Failed to load data'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => { setEditId(null); setForm({ ...EMPTY_FORM }); setDialogOpen(true); };
  const openEdit = (item) => {
    setEditId(item.id);
    setForm({ name: item.name || '', email: item.email || '', phone: item.phone || '', address: item.address || '', city: item.city || '', state: item.state || '', zip: item.zip || '', country: item.country || '', gst_number: item.gst_number || '' });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name) { toast.error('Name is required'); return; }
    setSaving(true);
    const endpoint = tab === 'clients' ? '/clients' : '/vendors';
    try {
      if (editId) {
        await api.put(`${endpoint}/${editId}`, form);
        toast.success(`${tab === 'clients' ? 'Client' : 'Vendor'} updated`);
      } else {
        await api.post(endpoint, form);
        toast.success(`${tab === 'clients' ? 'Client' : 'Vendor'} created`);
      }
      setDialogOpen(false);
      fetchData();
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    const endpoint = tab === 'clients' ? '/clients' : '/vendors';
    try { await api.delete(`${endpoint}/${id}`); toast.success('Deleted'); fetchData(); }
    catch { toast.error('Failed to delete'); }
  };

  const items = tab === 'clients' ? clients : vendors;
  const label = tab === 'clients' ? 'Client' : 'Vendor';

  return (
    <div className="space-y-6 animate-fade-in" data-testid="clients-vendors-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Clients & Vendors</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage your business contacts</p>
        </div>
        <Button onClick={openCreate} className="bg-blue-600 hover:bg-blue-700 text-white gap-2" data-testid="create-contact-button">
          <Plus size={16} /> New {label}
        </Button>
      </div>

      {/* Tab Toggle */}
      <div className="flex gap-1 p-1 rounded-lg bg-card border border-border w-fit">
        <button onClick={() => setTab('clients')} className={`px-4 py-1.5 rounded-md text-sm transition-colors ${tab === 'clients' ? 'bg-blue-600 text-white' : 'text-muted-foreground hover:text-foreground'}`} data-testid="tab-clients">Clients</button>
        <button onClick={() => setTab('vendors')} className={`px-4 py-1.5 rounded-md text-sm transition-colors ${tab === 'vendors' ? 'bg-blue-600 text-white' : 'text-muted-foreground hover:text-foreground'}`} data-testid="tab-vendors">Vendors</button>
      </div>

      <Card className="bg-card border-border">
        <CardContent className="p-0">
          <Table className="data-table">
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-muted-foreground">Name</TableHead>
                <TableHead className="text-muted-foreground">Email</TableHead>
                <TableHead className="text-muted-foreground">Phone</TableHead>
                <TableHead className="text-muted-foreground">Address</TableHead>
                <TableHead className="text-muted-foreground">GST #</TableHead>
                <TableHead className="text-muted-foreground">Created</TableHead>
                <TableHead className="text-muted-foreground text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow className="border-border"><TableCell colSpan={7} className="text-center text-muted-foreground py-8"><Loader2 className="animate-spin inline" size={16} /> Loading...</TableCell></TableRow>
              ) : items.length === 0 ? (
                <TableRow className="border-border"><TableCell colSpan={7} className="text-center text-muted-foreground py-8">No {tab} yet. Add one to get started.</TableCell></TableRow>
              ) : items.map(item => (
                <TableRow key={item.id} className="border-border hover:bg-muted/50" data-testid={`contact-row-${item.id}`}>
                  <TableCell className="text-foreground font-medium">{item.name}</TableCell>
                  <TableCell className="text-muted-foreground text-sm">{item.email || '-'}</TableCell>
                  <TableCell className="text-muted-foreground text-sm">{item.phone || '-'}</TableCell>
                  <TableCell className="text-muted-foreground text-sm max-w-[200px] truncate">{[item.address, item.city, item.state, item.zip, item.country].filter(Boolean).join(', ') || '-'}</TableCell>
                  <TableCell className="text-muted-foreground text-sm font-mono">{item.gst_number || '-'}</TableCell>
                  <TableCell className="text-muted-foreground text-sm">{formatDate(item.created_at)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button size="sm" variant="ghost" className="text-blue-400 h-7 px-2" onClick={() => openEdit(item)} data-testid={`edit-contact-${item.id}`} title="Edit"><Pencil size={14} /></Button>
                      <Button size="sm" variant="ghost" className="text-muted-foreground hover:text-red-400 h-7 px-2" onClick={() => handleDelete(item.id)} data-testid={`delete-contact-${item.id}`} title="Delete"><Trash2 size={14} /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg bg-card border-border" data-testid="contact-dialog">
          <DialogHeader>
            <DialogTitle className="text-card-foreground">{editId ? `Edit ${label}` : `New ${label}`}</DialogTitle>
            <DialogDescription className="text-muted-foreground">Enter the {label.toLowerCase()} details</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-card-foreground/80 text-sm">Name *</Label>
                <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="bg-[hsl(var(--surface-input))] border-input text-foreground" data-testid="contact-name-input" placeholder={`${label} name`} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-card-foreground/80 text-sm">Email</Label>
                <Input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="bg-[hsl(var(--surface-input))] border-input text-foreground" data-testid="contact-email-input" placeholder="email@company.com" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-card-foreground/80 text-sm">Phone</Label>
                <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="bg-[hsl(var(--surface-input))] border-input text-foreground" data-testid="contact-phone-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-card-foreground/80 text-sm">GST/Tax Number</Label>
                <Input value={form.gst_number} onChange={e => setForm({ ...form, gst_number: e.target.value })} className="bg-[hsl(var(--surface-input))] border-input text-foreground" data-testid="contact-gst-input" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-card-foreground/80 text-sm">Address</Label>
              <Input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} className="bg-[hsl(var(--surface-input))] border-input text-foreground" data-testid="contact-address-input" placeholder="Street address" />
            </div>
            <div className="grid grid-cols-4 gap-3">
              <div className="space-y-1.5">
                <Label className="text-card-foreground/80 text-sm">City</Label>
                <Input value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} className="bg-[hsl(var(--surface-input))] border-input text-foreground" data-testid="contact-city-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-card-foreground/80 text-sm">State</Label>
                <Input value={form.state} onChange={e => setForm({ ...form, state: e.target.value })} className="bg-[hsl(var(--surface-input))] border-input text-foreground" data-testid="contact-state-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-card-foreground/80 text-sm">ZIP / Pincode</Label>
                <Input value={form.zip} onChange={e => setForm({ ...form, zip: e.target.value })} className="bg-[hsl(var(--surface-input))] border-input text-foreground" data-testid="contact-zip-input" placeholder="e.g. 400001" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-card-foreground/80 text-sm">Country</Label>
                <Input value={form.country} onChange={e => setForm({ ...form, country: e.target.value })} className="bg-[hsl(var(--surface-input))] border-input text-foreground" data-testid="contact-country-input" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-input text-card-foreground/80">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-contact-button">
              {saving ? <Loader2 className="animate-spin" size={16} /> : (editId ? 'Update' : 'Create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
